'use client';

import Link from 'next/link';
import type { NearbySportEvent } from '@bip/api-client';
// Reuse the sport card's CSS — visual layout is identical (same
// row order, same footer). Only the routing + tournament block
// differ, both handled in the JSX below.
import './NearbySportCard.css';

/**
 * `NearbyConcertCard` — sibling of `NearbySportCard`, tuned for the
 * concerts domain:
 *
 *   • "Book Now" routes to `/concerts/[id]` (not `/sports/[id]`).
 *   • Tournament row is suppressed — concerts don't have tournaments,
 *     so the row would either render blank or leak stale sports-domain
 *     league names that arrive on the wire for legacy events.
 *
 * Everything else — category strip, name, location, date/time,
 * distance, "From £X" — matches `NearbySportCard` byte-for-byte
 * (deliberate CSS reuse via `NearbySportCard.css`).
 */
function formatEventDate(dateOfEvent: string, timeOfEvent: string): string {
  try {
    const [dd, mm, yyyy] = dateOfEvent.split('/');
    const d = new Date(`${yyyy}-${mm}-${dd}T${timeOfEvent}`);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const day = d.getDate();
    const sfx = ['th', 'st', 'nd', 'rd'][day % 10 <= 3 && Math.floor(day / 10) !== 1 ? day % 10 : 0];
    return `${days[d.getDay()]}, ${day}${sfx} ${months[d.getMonth()]} ${d.getFullYear()} - ${timeOfEvent}`;
  } catch {
    return `${dateOfEvent} - ${timeOfEvent}`;
  }
}

export function NearbyConcertCard({
  concert,
  referenceName,
}: {
  concert: NearbySportEvent;
  referenceName?: string;
}) {
  return (
    <div className="rns-card">
      <div className="rns-topstrip">
        {/* <span className="rns-category">{concert.eventType?.name || 'Concert'}</span> */}
      </div>

      <div className="rns-body">
        <div className="rns-name">{concert.name}</div>

        {/* Location row — carries `city, country` PLUS the "N miles
            away from {venue}" suffix on the SAME line. Trailing the
            location keeps a single "where"-flavoured row under the
            map-marker icon (calendar row below stays clean of
            distance text, which is what pushed the calendar icon
            out of alignment before). */}
        <div className="rns-meta">
          <i className="text-lg fal fa-map-marker-alt rns-meta__icon" />
          {[concert.city?.name, concert.country?.name].filter(Boolean).join(', ')}
          {concert.distanceInMiles != null && (
            <>
              , {concert.distanceInMiles.toFixed(1)} miles away
              {referenceName ? ` from ${referenceName}` : ''}
            </>
          )}
        </div>

        <div className="rns-meta">
          <i className="text-lg fal fa-calendar rns-meta__icon" />
          {formatEventDate(concert.dateOfEvent, concert.timeOfEvent)}
        </div>

        {concert.minTicketPrice?.price != null && (
          <div className="rns-price">
            From {concert.minTicketPrice.currencySymbol}
            {concert.minTicketPrice.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
          </div>
        )}
      </div>

      <div className="rns-footer">
        <Link
          href={concert.detailPageUrl ?? `/concerts/${concert.id}`}
          className="rns-book"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book Now
        </Link>
      </div>
    </div>
  );
}
